import { useTranslation } from 'react-i18next';
import { AssistantAskButton } from '@/components/assistant/AssistantAskButton';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { sortAppointmentsByStartTime } from '../utils/appointmentFiltering';
import {
  isAppointmentCompleted,
  resolveAppointmentStatusCode,
  resolveCaretakerDisplayName,
  resolvePatientDisplayName,
} from '../utils/appointmentDisplay';
import type { DayDetailsPanelProps } from '../types';

export function DayDetailsPanel({
  selectedDate,
  appointmentsForSelectedDate,
  onClose,
  onEditAppointment,
  onPreviewAppointment,
  onOpenSessionNote,
  onAddAppointment,
}: DayDetailsPanelProps) {
  const { t } = useTranslation();

  const sortedAppointments = sortAppointmentsByStartTime(appointmentsForSelectedDate);
  const patientFallbackText = t('appointments:patientFallback');
  const caretakerUnknownText = t('appointments:caretakerUnknown');

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-zoowhite shadow-2xl z-40 flex flex-col animate-in slide-in-from-right duration-300 border-l border-zooborder">
      <div className="p-6 border-b border-zooborder flex justify-between items-center bg-zoovpalelavender bg-opacity-30">
        <div>
          <h3 className="text-lg font-bold text-zooindigo">
            {t('calendar:dayDetails.title', { date: formatDateToDayMonthYear(selectedDate) })}
          </h3>
          <p className="text-xs text-zoomuted mt-1">
            {t('calendar:dayDetails.scheduledCount', { count: sortedAppointments.length })}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <Icon name="X" className="text-zooindigo" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        {sortedAppointments.length > 0 ? (
          <div className="space-y-4">
            {sortedAppointments.map(function renderAppointmentRow(appointment) {
              const patientDisplayName = resolvePatientDisplayName(
                appointment,
                patientFallbackText
              );
              return (
                <AssistantTargetRegion
                  key={appointment.id}
                  targetKey={ASSISTANT_TARGET_KEY.dashboardAppointmentRow}
                  entityId={appointment.id}
                >
                  <Card
                    padding="none"
                    shadow={false}
                    className="p-4 hover:border-zooprimary transition-all group"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-bold text-zooprimary">
                          {appointment.startTime}
                        </div>
                        <div className="w-1 h-1 rounded-full bg-zooviolet" />
                        <div className="text-sm font-bold text-zooindigo">{patientDisplayName}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          tone={isAppointmentCompleted(appointment) ? 'success' : 'neutral'}
                          uppercase
                        >
                          {t(
                            buildAppointmentStatusTranslationKey(
                              resolveAppointmentStatusCode(appointment)
                            )
                          )}
                        </Badge>
                        <AssistantAskButton
                          subject={patientDisplayName}
                          question={t('assistant:askAbout.appointment', {
                            name: patientDisplayName,
                            date: formatDateToDayMonthYear(selectedDate),
                            time: appointment.startTime,
                          })}
                        />
                      </div>
                    </div>

                    <div className="text-xs text-zoomuted mb-4">
                      <p>
                        <span className="font-medium">{t('appointments:fields.caretaker')}:</span>{' '}
                        {resolveCaretakerDisplayName(appointment, caretakerUnknownText)}
                      </p>
                      <p>
                        <span className="font-medium">{t('appointments:fields.treatment')}:</span>{' '}
                        {t(buildTherapyTypeTranslationKey(appointment.therapyType))}
                      </p>
                      {appointment.note.length > 0 && (
                        <p className="mt-2 italic">&quot;{appointment.note}&quot;</p>
                      )}
                    </div>

                    <div className="pt-3 border-t border-zooborder space-y-2">
                      <Button
                        size="small"
                        onClick={function openNoteForThisAppointment() {
                          onOpenSessionNote(appointment);
                        }}
                        className="w-full text-xs flex items-center justify-center gap-1.5"
                      >
                        <Icon name="FileText" /> {t('patients:sessionNote.editTitle')}
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={function previewThisAppointment() {
                            onPreviewAppointment(appointment);
                          }}
                          className="flex-1 text-xs flex items-center justify-center gap-1"
                        >
                          <Icon name="Eye" /> {t('common:actions.preview')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={function editThisAppointment() {
                            onEditAppointment(appointment);
                          }}
                          className="flex-1 text-xs flex items-center justify-center gap-1"
                        >
                          <Icon name="Edit" /> {t('common:actions.edit')}
                        </Button>
                      </div>
                    </div>
                  </Card>
                </AssistantTargetRegion>
              );
            })}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-20 h-20 bg-zoovpalelavender rounded-full flex items-center justify-center mb-4">
              <Icon name="Calendar" className="text-4xl text-zooviolet opacity-40" />
            </div>
            <h4 className="text-zooindigo font-bold mb-2">{t('calendar:dayDetails.emptyTitle')}</h4>
            <p className="text-zoomuted text-sm mb-6">
              {t('calendar:dayDetails.emptyDescription')}
            </p>
          </div>
        )}
      </div>

      <div className="p-6 border-t border-zooborder">
        <Button
          onClick={function addAppointmentForSelectedDate() {
            onAddAppointment(selectedDate);
          }}
          className="w-full flex items-center justify-center gap-2"
        >
          <Icon name="Plus" /> {t('calendar:addAppointment')}
        </Button>
      </div>
    </div>
  );
}
